import { MaterialInput } from 'app/components';
import { Container, IAppState } from 'app/containers/Container'
import { getAuthErrorMessage, isAlreadyConfirmedError, UserActions } from 'app/redux/User/actions'
import * as React from 'react'
import { Alert, Button, Col, FormGroup, Row } from 'react-bootstrap'
import { Link } from 'react-router-dom'

interface IConfirmState {
  username: string
  confirmcode: string
  error: string
  isConfirming: boolean
  isResending: boolean
  success: string
  info: string
}

function parseHashSearch(hash: string): { [key: string]: string } {
  const search: { [key: string]: string } = {}
  const questionIndex = hash.indexOf('?')

  if (questionIndex < 0) {
    return search
  }

  hash.substring(questionIndex + 1).split('&').forEach((elem) => {
    const equalsIndex = elem.indexOf('=')

    if (equalsIndex < 0) {
      return
    }

    const key = decodeURIComponent(elem.substring(0, equalsIndex).replace(/\+/g, ' '))
    const value = decodeURIComponent(elem.substring(equalsIndex + 1).replace(/\+/g, ' '))
    search[key] = value
  })

  return search
}

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: 'calc(100vh - 64px)',
    padding: '54px 0 70px',
  },
  shell: {
    background: '#ffffff',
    border: '1px solid rgba(2, 38, 54, 0.08)',
    borderRadius: 8,
    boxShadow: '0 24px 60px rgba(2, 38, 54, 0.14)',
    display: 'flex',
    flexWrap: 'wrap',
    overflow: 'hidden',
  },
  brandPanel: {
    background: 'linear-gradient(135deg, #022636 0%, #062d4e 58%, #0070C0 100%)',
    color: '#ffffff',
    flex: '1 1 280px',
    minHeight: 470,
    padding: '42px 38px',
    position: 'relative',
  },
  brandRule: {
    background: '#00B050',
    borderRadius: 4,
    height: 4,
    marginBottom: 28,
    width: 64,
  },
  eyebrow: {
    color: 'rgba(255, 255, 255, 0.74)',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1.6,
    marginBottom: 18,
    textTransform: 'uppercase',
  },
  title: {
    color: '#ffffff',
    fontSize: 34,
    fontWeight: 300,
    lineHeight: 1.18,
    margin: '0 0 18px',
  },
  brandCopy: {
    color: 'rgba(255, 255, 255, 0.82)',
    fontSize: 15,
    lineHeight: 1.7,
    marginBottom: 34,
    maxWidth: 380,
  },
  noteCard: {
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.16)',
    borderRadius: 8,
    marginTop: 26,
    padding: 20,
  },
  noteLabel: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 12,
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  noteValue: {
    color: '#ffffff',
    fontSize: 18,
    lineHeight: 1.45,
    margin: 0,
  },
  formPanel: {
    flex: '1 1 380px',
    padding: '42px 42px 36px',
  },
  formTitle: {
    color: '#022636',
    fontSize: 26,
    fontWeight: 500,
    margin: '0 0 8px',
  },
  formCopy: {
    color: '#667983',
    fontSize: 15,
    lineHeight: 1.6,
    margin: '0 0 26px',
  },
  inputGroup: {
    background: '#f7fafb',
    border: '1px solid #e3ebef',
    borderRadius: 8,
    marginBottom: 18,
    padding: '2px 16px 0',
  },
  primaryButton: {
    background: '#00B050',
    border: 'none',
    borderRadius: 4,
    boxShadow: '0 8px 18px rgba(0, 176, 80, 0.28)',
    fontSize: 15,
    fontWeight: 600,
    height: 48,
    marginBottom: 12,
    width: '100%',
  },
  secondaryButton: {
    background: '#ffffff',
    border: '1px solid #d9e5eb',
    borderRadius: 4,
    color: '#022636',
    fontSize: 14,
    fontWeight: 600,
    height: 44,
    width: '100%',
  },
  footer: {
    borderTop: '1px solid #edf2f4',
    color: '#667983',
    fontSize: 14,
    marginTop: 24,
    paddingTop: 22,
    textAlign: 'center',
  },
  loginLink: {
    color: '#0070C0',
    fontWeight: 700,
  },
}

class Confirm extends Container<{}, IConfirmState> {
  private linkConfirmationStarted = false
  private redirectTimer: number

  state = {
    username: '',
    confirmcode: '',
    error: null,
    isConfirming: false,
    isResending: false,
    success: '',
    info: '',
  }

  mapAppStateToContainerState(appState: IAppState): IConfirmState {
    return this.state
  }

  componentDidMount() {
    const locationSearch = parseHashSearch(location.hash)
    const username = locationSearch.username || ''
    const confirmcode = locationSearch.confirm || ''
    let info = ''

    if (locationSearch.from === 'signup') {
      info = 'Please check your email for the verification link or enter the confirmation code below.'
    } else if (locationSearch.reason === 'not-confirmed') {
      info = 'Your account must be confirmed before you can sign in.'
    }

    this.setState({ username, confirmcode, info })

    if (username && confirmcode) {
      this.confirmAccount(username, confirmcode)
    } else if (confirmcode && !username) {
      this.setState({ error: getAuthErrorMessage({ code: 'MissingUsername' }) })
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount()

    if (this.redirectTimer) {
      window.clearTimeout(this.redirectTimer)
    }
  }

  confirm(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    this.confirmAccount(this.state.username, this.state.confirmcode)
  }

  resendCode() {
    const username = this.state.username

    if (!username) {
      this.setState({ error: getAuthErrorMessage({ code: 'MissingUsername' }), success: '' })
      return
    }

    this.setState({ error: null, success: '', isResending: true })

    UserActions.resendConfirmationCode(username)
      .then(() => {
        this.setState({
          isResending: false,
          success: 'A new verification code has been sent. Please check your email.',
        })
      })
      .catch((error) => {
        // tslint:disable-next-line:no-console
        console.error('Unable to resend confirmation code', error)
        this.setState({ error: getAuthErrorMessage(error), isResending: false })
      })
  }

  setPartialState(attr: string, value: any) {
    const updateState = {}
    updateState[attr] = value
    updateState['error'] = null
    updateState['success'] = ''

    this.setState(updateState)
  }

  private confirmAccount(username: string, confirmcode: string) {
    if (this.state.isConfirming || this.linkConfirmationStarted) {
      return
    }

    if (!username) {
      this.setState({ error: getAuthErrorMessage({ code: 'MissingUsername' }), success: '' })
      return
    }

    if (!confirmcode) {
      this.setState({ error: getAuthErrorMessage({ code: 'MissingConfirmationCode' }), success: '' })
      return
    }

    this.linkConfirmationStarted = true
    this.setState({ error: null, success: '', isConfirming: true })

    UserActions.confirm(username, confirmcode)
      .then(() => {
        this.setState({
          isConfirming: false,
          success: 'Your account has been confirmed. You can now sign in.',
        })

        this.redirectTimer = window.setTimeout(() => location.hash = '#/login', 2500)
      })
      .catch((error) => {
        // tslint:disable-next-line:no-console
        console.error('Unable to confirm user registration', error)

        if (isAlreadyConfirmedError(error)) {
          this.setState({
            isConfirming: false,
            success: 'This account is already confirmed. You can now sign in.',
          })
          this.redirectTimer = window.setTimeout(() => location.hash = '#/login', 2500)
          return
        }

        this.linkConfirmationStarted = false
        this.setState({ error: getAuthErrorMessage(error), isConfirming: false })
      })
  }

  render() {
    return (
      <section style={styles.page}>
        <section className="container">
          <Row>
            <Col mdOffset={1} md={10}>
              <div className="animated fadeIn" style={styles.shell}>
                <aside style={styles.brandPanel}>
                  <div style={styles.brandRule} />
                  <div style={styles.eyebrow}>Verify Your Account</div>
                  <h1 style={styles.title}>Confirm your FoundationMark registration.</h1>
                  <p style={styles.brandCopy}>
                    Enter the code from your confirmation email to finish setting up your portal access.
                  </p>
                  <div style={styles.noteCard}>
                    <div style={styles.noteLabel}>Need another code?</div>
                    <p style={styles.noteValue}>
                      Add your username or email, then resend a fresh verification code.
                    </p>
                  </div>
                </aside>

                <div style={styles.formPanel}>
                  <h2 style={styles.formTitle}>Confirm account</h2>
                  <p style={styles.formCopy}>Use the verification details sent to your email.</p>

                  {this.state.info ?
                    <Alert bsStyle="info" className="animated fadeIn">
                      {this.state.info}
                    </Alert>
                    : null
                  }

                  {this.state.isConfirming ?
                    <Alert bsStyle="info" className="animated fadeIn">
                      Confirming your account...
                    </Alert>
                    : null
                  }

                  {this.state.success ?
                    <Alert bsStyle="success" className="animated fadeIn">
                      {this.state.success}
                    </Alert>
                    : null
                  }

                  <form name="confirmForm" onSubmit={(e) => this.confirm(e)}>
                    <FormGroup style={styles.inputGroup}>
                      <MaterialInput placeholder="Username or email"
                        required={true}
                        disabled={this.state.isConfirming}
                        onChange={(value) => this.setPartialState('username', value)}
                        value={this.state.username} />
                    </FormGroup>
                    <FormGroup style={styles.inputGroup}>
                      <MaterialInput placeholder="Confirmation code"
                        required={true}
                        disabled={this.state.isConfirming}
                        onChange={(value) => this.setPartialState('confirmcode', value)}
                        value={this.state.confirmcode} />
                    </FormGroup>
                    <FormGroup>
                      <Button type="submit" bsStyle="success" disabled={this.state.isConfirming} style={styles.primaryButton}>
                        <i className="fa fa-fw fa-check-circle" />
                        {this.state.isConfirming ? 'Confirming...' : 'Confirm Account'}
                      </Button>
                      <Button type="button"
                        bsStyle="default"
                        disabled={this.state.isConfirming || this.state.isResending}
                        onClick={() => this.resendCode()}
                        style={styles.secondaryButton}>
                        <i className="fa fa-fw fa-envelope-o" />
                        {this.state.isResending ? 'Sending...' : 'Resend verification code'}
                      </Button>
                    </FormGroup>

                    {this.state.error ?
                      <FormGroup>
                        <Alert bsStyle="danger" className="animated fadeIn">
                          <strong>
                            <i className="fa fa-fw fa-exclamation-circle" />
                            Uh oh!
                          </strong>
                          <span>
                            &nbsp;{this.state.error}
                          </span>
                        </Alert>
                      </FormGroup>
                      : null
                    }
                  </form>

                  <div style={styles.footer}>
                    <span>Already confirmed?&nbsp;</span>
                    <Link to="/login" style={styles.loginLink}>Sign in here</Link>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </section>
      </section>
    )
  }
}

export {
  Confirm,
  parseHashSearch
}



// WEBPACK FOOTER //
// ./src/app/containers/Confirm/index.tsx